import { createFileRoute } from "@tanstack/react-router"
import { IconGavel, IconArrowRight, IconHeart, IconShield, IconFileText } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import {
  Card,
  Button,
  Text,
  Center,
  Box,
  useMantineColorScheme,
  ActionIcon,
  Title,
  Stack,
  Grid,
  Container,
  Group,
  List,
} from "@mantine/core"
import { SEO } from "@/components/seo/SEO"
import { ShareButtons } from "@/components/ui/ShareButtons"
import { StructuredData, createFAQPageSchema, type FAQ } from "@/components/seo/StructuredData"
import { FAQSection } from "@/components/ui/FAQSection"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/faith")({
  component: FaithLandingPage,
})

const faithFAQs: FAQ[] = [
  {
    question: "Why should Christians care about the Epstein case?",
    answer:
      "Biblical teachings command us to defend the innocent, expose evil, and pursue justice without partiality. The Epstein case involves serious crimes against children and vulnerable people. As people of faith, we cannot remain silent when the powerful escape accountability. Scripture calls us to 'speak up for those who cannot speak for themselves' (Proverbs 31:8) and to 'have nothing to do with the fruitless deeds of darkness, but rather expose them' (Ephesians 5:11). Demanding complete transparency is a moral and spiritual duty.",
  },
  {
    question: "How does faith inform our response to government manipulation?",
    answer:
      "Our faith teaches us to seek truth above all else. Recent government 'releases' contain 97% previously public material, which is deception rather than transparency. As Christians, we are called to expose lies and stand for truth, even when it's uncomfortable. Jesus condemned the religious and political leaders who manipulated the truth for their own benefit. We must demand complete, unredacted disclosure and reject half-truths that protect the powerful while deceiving the public.",
  },
  {
    question: "What does the Bible say about protecting children?",
    answer:
      "Jesus spoke strongly about protecting children, saying 'If anyone causes one of these little ones to stumble, it would be better for them to have a large millstone hung around their neck' (Matthew 18:6). Throughout Scripture, God calls His people to defend the vulnerable, the fatherless, and the oppressed. Psalm 82:3-4 commands us to 'Defend the weak and the fatherless; uphold the cause of the poor and the oppressed. Rescue the weak and the needy.' Our faith demands fierce advocacy for children and zero tolerance for those who harm them, regardless of their power or status.",
  },
  {
    question: "How can people of faith take action?",
    answer:
      "You can honor God's call to justice by: (1) Contacting your representatives to demand complete, unredacted file releases, (2) Educating your church community about the importance of accountability and transparency, (3) Praying for victims and their families, (4) Sharing verified information to combat government disinformation, and (5) Supporting organizations working for justice and child protection. James 2:17 reminds us that 'faith by itself, if it is not accompanied by action, is dead.' Our faith calls us to both prayer and practical action.",
  },
  {
    question: "Is demanding justice compatible with forgiveness?",
    answer:
      "Yes, absolutely. Biblical forgiveness does not mean abandoning justice or accountability. God is both merciful and just. Romans 13:4 describes governing authorities as 'God's servant, an agent of wrath to bring punishment on the wrongdoer.' We can forgive those who have sinned while still demanding they face earthly consequences for their crimes. True repentance includes accepting accountability. Protecting future victims and ensuring justice for past victims is an act of love and obedience to God's command to defend the innocent.",
  },
]

function FaithLandingPage() {
  const { colorScheme } = useMantineColorScheme()

  return (
    <>
      <SEO
        title="Faith & Justice: Defending the Innocent"
        description="As people of faith, we are called to pursue truth, defend the innocent, and hold the powerful accountable. Join us in demanding complete transparency in the Jeffrey Epstein case."
        canonical="https://rtefn.com/faith"
        keywords="Christian accountability, biblical justice, protecting children, religious transparency, faith-based advocacy, Christian response to Epstein case, defending the innocent, exposing evil, moral duty, spiritual responsibility"
        ogType="website"
        ogImage="https://rtefn.com/og-faith.png"
        ogImageAlt="Faith & Justice - Release The Epstein Files Now"
      />
      <StructuredData data={createFAQPageSchema(faithFAQs)} />
      <Box className="animate-fade-in">
        {/* Hero Section */}
        <Box
          className={`min-h-screen flex items-center justify-center relative overflow-hidden py-16 ${
            colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-amber-50 via-white to-blue-50"
          }`}
        >
          {colorScheme === "dark" && <Box className="noise-overlay" />}

          <Container size="xl" className="relative z-10">
            <Stack ta="center" maw={1280} mx="auto">
              {/* Tagline */}
              <Box className="animate-fade-in-up" style={{ animationDelay: "0.2s", marginBottom: "var(--space-3xl)" }}>
                <Box
                  component="span"
                  className="inline-block backdrop-blur-md text-lg font-medium rounded-full"
                  style={{
                    letterSpacing: "0.025em",
                    padding: "12px 28px",
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(217, 119, 6, 0.08)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(217, 119, 6, 0.2)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(146, 64, 14, 1)",
                  }}
                >
                  Truth • Justice • Righteousness
                </Box>
              </Box>

              {/* Main Headline */}
              <h1
                className="animate-fade-in-up"
                style={{
                  animationDelay: "0.4s",
                  fontFamily: "var(--font-family)",
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 0.95,
                  fontSize: "clamp(3rem, 8vw, 7.5rem)",
                  margin: "0 0 var(--space-2xl) 0",
                  color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                }}
              >
                Defending the Innocent
                <br />
                <span className="gradient-text">Pursuing Truth</span>
              </h1>

              {/* Subtitle */}
              <div className="animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
                <p
                  className="max-w-4xl mx-auto"
                  style={{
                    fontFamily: "var(--font-family)",
                    fontWeight: 400,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.5,
                    fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#6b7280",
                    margin: "0 0 var(--space-3xl) 0",
                  }}
                >
                  &ldquo;Learn to do right; seek justice. Defend the oppressed. Take up the cause of the fatherless;
                  plead the case of the widow.&rdquo; - Isaiah 1:17
                </p>
              </div>

              {/* CTA Buttons */}
              <Box className="animate-fade-in-up" mt="xl" mb="3xl" style={{ animationDelay: "0.8s" }}>
                <PageCTASection
                  contextualAction={{ href: "/documents", label: "Explore Evidence", icon: IconFileText }}
                />
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* Biblical Call to Justice */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0a0a0a 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #fef3c7 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Box maw={1152} mx="auto">
              <Card
                shadow="lg"
                radius="xl"
                p="3xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(217, 119, 6, 0.2)",
                }}
              >
                <Stack gap="xl">
                  <Group justify="center" gap="md" align="center">
                    <ActionIcon
                      variant="subtle"
                      size={64}
                      radius="md"
                      style={{ color: colorScheme === "dark" ? "#f59e0b" : "#d97706" }}
                    >
                      <IconHeart size={40} />
                    </ActionIcon>
                    <Title order={2} size="3rem" fw={700}>
                      Our <span style={{ color: colorScheme === "dark" ? "#f59e0b" : "#d97706" }}>Sacred Duty</span>
                    </Title>
                  </Group>

                  <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center">
                    As people of faith, we are called to be <strong>light in the darkness</strong> and to{" "}
                    <strong>pursue righteousness</strong>. The Jeffrey Epstein case involves heinous crimes against
                    children and vulnerable people. We cannot remain silent when the powerful escape accountability. God
                    calls us to <strong>defend the innocent</strong>, expose evil, and demand truth—regardless of who it
                    implicates. Recent government "releases" contain 97% previously public material. This is deception,
                    not transparency. We demand <strong>complete, unredacted truth</strong>.
                  </Text>

                  <Box mt="lg">
                    <Stack gap="md">
                      <Text size="lg" fw={600} ta="center" c="dimmed">
                        Scripture Calls Us to Action:
                      </Text>
                      <List spacing="md" size="md" center>
                        <List.Item>
                          <strong>Psalm 82:3-4</strong> - &ldquo;Defend the weak and the fatherless; uphold the cause of
                          the poor and the oppressed. Rescue the weak and the needy.&rdquo;
                        </List.Item>
                        <List.Item>
                          <strong>Proverbs 21:15</strong> - &ldquo;When justice is done, it brings joy to the righteous
                          but terror to evildoers.&rdquo;
                        </List.Item>
                        <List.Item>
                          <strong>Micah 6:8</strong> - &ldquo;He has shown you, O mortal, what is good. And what does
                          the Lord require of you? To act justly and to love mercy and to walk humbly with your
                          God.&rdquo;
                        </List.Item>
                        <List.Item>
                          <strong>Ephesians 5:11</strong> - &ldquo;Have nothing to do with the fruitless deeds of
                          darkness, but rather expose them.&rdquo;
                        </List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Why This Matters to People of Faith */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0f0f23 0%, #1a1a2e 100%)"
                : "linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Stack ta="center" mb="4xl">
              <Title order={2} size="3rem" fw={700} mb="xl" ta="center">
                Why This <span style={{ color: colorScheme === "dark" ? "#f59e0b" : "#d97706" }}>Matters</span>
              </Title>
              <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center" c="dimmed" mb="xl">
                Our faith compels us to protect the vulnerable and pursue justice without compromise
              </Text>
            </Stack>

            <Grid gutter="xl">
              {/* Protecting Children */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(217, 119, 6, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#d97706" : "#f59e0b",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconShield size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Protecting the Innocent
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Jesus said, &ldquo;If anyone causes one of these little ones to stumble, it would be better for
                      them to have a large millstone hung around their neck.&rdquo; Children deserve our fierce
                      protection and advocacy.
                    </Text>

                    <Button
                      component={Link}
                      to="/documents"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(217, 119, 6, 0.15)" : "rgba(217, 119, 6, 0.08)",
                        color: colorScheme === "dark" ? "#fbbf24" : "#d97706",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(217, 119, 6, 0.3)" : "rgba(217, 119, 6, 0.2)"}`,
                      }}
                    >
                      Review Evidence
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Exposing Darkness */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(217, 119, 6, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#d97706" : "#f59e0b",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconGavel size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Exposing Darkness
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      We are commanded to expose evil, not hide it. Powerful people have used their influence to evade
                      justice. Truth must come to light—no matter who is implicated or how uncomfortable it makes us.
                    </Text>

                    <Button
                      component={Link}
                      to="/timeline"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(217, 119, 6, 0.15)" : "rgba(217, 119, 6, 0.08)",
                        color: colorScheme === "dark" ? "#fbbf24" : "#d97706",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(217, 119, 6, 0.3)" : "rgba(217, 119, 6, 0.2)"}`,
                      }}
                    >
                      View Timeline
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Accountability */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(217, 119, 6, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#d97706" : "#f59e0b",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconGavel size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Demanding Accountability
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Justice requires accountability for ALL—rich or poor, powerful or powerless. God shows no
                      partiality, and neither should our justice system. We must demand equal treatment under the law.
                    </Text>

                    <Button
                      component={Link}
                      to="/news"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(217, 119, 6, 0.15)" : "rgba(217, 119, 6, 0.08)",
                        color: colorScheme === "dark" ? "#fbbf24" : "#d97706",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(217, 119, 6, 0.3)" : "rgba(217, 119, 6, 0.2)"}`,
                      }}
                    >
                      Latest Updates
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* FAQ Section */}
        <FAQSection
          faqs={faithFAQs}
          accentColor={colorScheme === "dark" ? "#f59e0b" : "#d97706"}
          title="Frequently Asked Questions"
          subtitle="Answers to common questions about faith, justice, and accountability"
        />

        {/* Call to Action */}
        <Box
          ta="center"
          style={{
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
            backgroundColor: colorScheme === "dark" ? "#030712" : "#78350f",
          }}
        >
          <Container size="xl">
            <Stack maw={896} mx="auto" ta="center">
              <Title order={2} size="3rem" fw={600} mb="md" c="white">
                Be a Voice for the Voiceless
              </Title>

              <Text size="xl" c="gray.3" mb="xl" maw={768} mx="auto" fw={300} lh={1.6}>
                &ldquo;Speak up for those who cannot speak for themselves, for the rights of all who are destitute.
                Speak up and judge fairly; defend the rights of the poor and needy.&rdquo; - Proverbs 31:8-9
              </Text>

              <Stack hiddenFrom="sm" gap="lg">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Stand for Truth
                </Link>
              </Stack>
              <Group visibleFrom="sm" gap="lg" justify="center">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Stand for Truth
                </Link>
              </Group>

              {/* Share Section */}
              <Box mt="2xl">
                <Text size="sm" c="gray.4" mb="sm" ta="center" fw={500}>
                  Share this message with your community
                </Text>
                <Group justify="center">
                  <ShareButtons size="lg" variant="filled" />
                </Group>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  )
}
